import roles from '@/constants/roles';
import { Base64 } from 'js-base64';
import $rq from '@/api/api';
import { User, TeamResponse, Team } from '@/interfaces/common';
import { UserLoginData } from '@/interfaces/apiInterfaces';
import { ActionContext, Dispatch } from 'vuex';
import type { RootState } from '@/interfaces/store';

// Define AuthState type based on your module's state structure
type AuthState = ReturnType<typeof getDefaultState>;
import { i18n } from '@/utilities/i18n';
import router from '@/router';
import { getPostLoginRoute } from '@/utilities/financialAdminRedirect';

// ---------------------------------------------
// Automatic access-token refresh helper
// ---------------------------------------------
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
const REFRESH_BUFFER_MS = 10 * 60 * 1000; // refresh 10 minutes before expiration

function scheduleAutoRefresh(exp: number, dispatchFn: Dispatch) {
    // exp is in seconds (standard JWT spec)
    if (!exp || typeof exp !== 'number') {
        return;
    }

    if (refreshTimer) {
        clearTimeout(refreshTimer);
        refreshTimer = null;
    }

    const currentTime = Date.now();
    const expirationTime = exp * 1000; // Convert to milliseconds
    const ttlMs = expirationTime - currentTime - REFRESH_BUFFER_MS;

    if (ttlMs <= 0) {
        // already expired or about to expire -> refresh immediately
        dispatchFn('refreshAccessTokenAndReloadAccessControlState');
        return;
    }

    // Ensure we don't schedule for more than 24 hours (safety check).
    const maxScheduleTime = 24 * 60 * 60 * 1000; // 24 hours in ms
    const actualTtl = Math.min(ttlMs, maxScheduleTime);

    refreshTimer = setTimeout(() => {
        dispatchFn('refreshAccessTokenAndReloadAccessControlState');
    }, actualTtl);
}

const getDefaultState = (lang = 'en') => ({
    status: '',
    user: {
        lang,
        id: '',
        email: '',
        teams: [],
        teamPagination: {},
        isFirstLogin: false,
    },
    currentUser: {},
    signup: {},
    selectedTeamIndex: -1,
    isLoggedIn: false,
    isFirstLoginSession: false,
    tokenExpiration: 0,
    verificationCode: '',
    currentTeam: null as Team | null,
    currentTeamId: null as null | string,
});

const defaultState = getDefaultState();

const mutations = {
    // Set authentification status
    authRequest(state: { status: string }) {
        state.status = 'loading';
    },
    authSuccess(
        state: {
            status: string;
            selectedTeam: number;
            user: { teams: string | unknown[] };
            selectedTeamIndex: number;
        },
        {
            headers,
            refreshToken,
            user,
        }: {
            headers: Record<string, string>;
            refreshToken: string;
            user: {
                lang: string;
                [key: string]: unknown;
            };
        },
    ) {
        state.status = 'success';
        state.selectedTeamIndex = -1;
        Object.assign(state.user, user);
        if (state.user.teams.length === 1) {
            state.selectedTeamIndex = 0;
        }
        $rq.setAxiosHeaders(headers);
        window.$cookies?.set('refreshToken', refreshToken, 30, true);

        window.$cookies?.set('lang', user.lang, 365, true);
    },
    // Logout user
    resetAuthState(state: { user: { lang: string | undefined } }) {
        // Reset state to default
        Object.assign(state, getDefaultState(state.user.lang));
    },
    // Set user team list
    setTeamList(state: { user: { teams: Team[] }; selectedTeamIndex: number }, list: unknown) {
        state.user.teams = Array.isArray(list) ? (list as Team[]) : [];
        if (state.user.teams.length === 1) {
            state.selectedTeamIndex = 0;
        } else {
            state.selectedTeamIndex = -1;
        }
    },
    setTeams(
        state: { user: { teams: unknown; teamPagination: unknown } },
        data: { teams: unknown; pagination: unknown },
    ) {
        state.user.teams = data.teams;
        state.user.teamPagination = data.pagination;
    },
    setSelectedTeam(
        state: { selectedTeamIndex: number; user: { teams: unknown[] } },
        selectedTeam: { id: string },
    ) {
        state.selectedTeamIndex = state.user.teams.findIndex(
            (team) => (team as { id: string }).id === selectedTeam.id,
        );
    },
    setInvitedTeamInfo(
        state: { invitedTeam: { id?: string; name?: string; invitationCode?: string } },
        obj: { team: { id: string; name: string }; invitationCode: string },
    ) {
        state.invitedTeam = {};
        state.invitedTeam.id = obj.team.id;
        state.invitedTeam.name = obj.team.name;
        state.invitedTeam.invitationCode = obj.invitationCode;
    },
    setInvitedUserInfo(
        state: {
            invitedUser: {
                id?: string;
                firstName?: string;
                lastName?: string;
                email?: string;
                preferences?: string;
            };
        },
        obj: {
            user: {
                id: string;
                profile: { firstName: string; lastName: string; email: string };
                preferences: string;
            };
        },
    ) {
        state.invitedUser = {};
        state.invitedUser.id = obj.user.id;
        state.invitedUser.firstName = obj.user.profile.firstName;
        state.invitedUser.lastName = obj.user.profile.lastName;
        state.invitedUser.email = obj.user.profile.email;
        state.invitedUser.preferences = obj.user.preferences;
    },
    setResetPwdCode(state: { resetPwdCode: string }, value: string) {
        state.resetPwdCode = value;
    },
    setLoggedIn(state: { isLoggedIn: boolean }, value: boolean) {
        state.isLoggedIn = value;
    },
    setFirstLoggedInSession(state: { isFirstLoginSession: boolean }, value: boolean) {
        state.isFirstLoginSession = value;
    },
    set(state: { isLoggedIn: boolean }, value: boolean) {
        state.isLoggedIn = value;
    },
    setLang(state: { user: { lang: string } }, lang: string) {
        state.user.lang = lang;
        window.$cookies?.set('lang', lang, 365, true);
    },
    setAccessToken(
        state: { tokenExpiration: string; user: { id: string } },
        obj: { token: string },
    ) {
        // Save access token in secure cookie
        window.$cookies?.set('accessToken', obj.token, 7, true);
        // Decode token without storing it, store userID and token expiration
        const decodedToken = JSON.parse(window.atob(obj.token.split('.')[1]));
        state.tokenExpiration = decodedToken.exp;
        state.user.id = decodedToken.user.id;
    },
    restoreUserFromToken(
        state: { tokenExpiration: number; user: { id: string; email: string } },
        tokenData: {
            exp: number;
            user?: { id?: string; email?: string };
            email?: string;
            sub?: string;
        },
    ) {
        // Try to get user info from nested user object or root level
        const userId = tokenData.user?.id || tokenData.sub;
        const userEmail = tokenData.user?.email || tokenData.email;

        if (userId) {
            state.user.id = userId;
        }
        if (userEmail) {
            state.user.email = userEmail;
        }
        state.tokenExpiration = tokenData.exp;
    },

    setSignupVerificationCode(state: { verificationCode: string }, code: string) {
        state.verificationCode = code;
    },
    setCurrentUser(state: { currentUser: User }, user: User) {
        state.currentUser = user;
    },
    SET_CURRENT_TEAM_DETAILS(state: AuthState, team: Team | null) {
        state.currentTeam = team;
        state.currentTeamId = team?.id || null;
        // Persist team ID to cookie for restoration on page refresh
        if (team?.id) {
            window.$cookies?.set('currentTeamId', team.id, 30, true);
        } else {
            window.$cookies?.remove('currentTeamId', true);
        }
    },
    setCypress(state: { user: { teams: { id: string }[] } }) {
        window.$cookies?.set('cypressTeamId', `${state.user.teams[0].id}`, 365, true);
    },
};

const actions = {
    setLang({ commit, getters }: ActionContext<AuthState, RootState>, newLang: string) {
        const lang = newLang || getters.user.lang || window.$cookies?.get('lang');
        i18n.global.locale.value = lang;
        window.$cookies?.set('lang', lang, 365, true);
        $rq.setAxiosHeaders({
            'X-AQ-Locale-Identifier': lang,
        });
        commit('setLang', lang);
    },
    async login(
        { dispatch, commit, getters }: ActionContext<AuthState, RootState>,
        user: UserLoginData,
    ) {
        dispatch('setLang', user.lang);
        commit('authRequest');
        const axiosHeaders = {
            'X-AQ-Locale-Identifier': user.lang,
        };
        try {
            const userData = await $rq.login(user);
            if (!userData) return;
            const setUser = {
                email: user.email,
                lang: user.lang,
                isFirstLogin: userData.isFirstLogin,
            };
            commit({
                type: 'authSuccess',
                refreshToken: userData.refreshToken,
                user: setUser,
                headers: axiosHeaders,
            });
            commit({
                type: 'setAccessToken',
                token: userData.accessToken,
            });
            // schedule auto-refresh based on new token
            if (userData.accessToken) {
                const decoded = JSON.parse(window.atob(userData.accessToken.split('.')[1]));
                scheduleAutoRefresh(decoded.exp, dispatch);
            }
            const urlParams = new URLSearchParams(window.location.search);
            const sso = urlParams.get('sso');
            const sig = urlParams.get('sig');

            if (sso && sig) {
                try {
                    const ssoResponse = await $rq.loginSso({ sso, sig });
                    if (ssoResponse?.data?.redirectUrl) {
                        window.location.href = ssoResponse.data.redirectUrl;
                        return;
                    }
                } catch (ssoError) {
                    console.error('SSO Error:', ssoError);
                    // Continue with normal flow if SSO fails
                }
            }

            // The login token has no team yet, so GET /teams/:id would 401; switchTeam reloads it once scoped.
            await dispatch('refreshUserTeams', { skipReloadTeam: true });
            const teams = (getters.getTeams || []) as Team[];
            const hasSingleTeam = teams.length === 1;
            if (hasSingleTeam) {
                await dispatch('switchTeam', teams[0]);
            }
            commit('setLoggedIn', true);
            commit('setFirstLoggedInSession', true);
            const redirectParam = new URLSearchParams(window.location.search).get('redirect');
            const redirectRoute = redirectParam ? decodeURIComponent(redirectParam) : '';
            if (redirectRoute && hasSingleTeam) {
                await router.replace(redirectRoute);
            } else if (redirectRoute) {
                await router.push({ path: '/teams', query: { redirect: redirectParam } });
            } else if (hasSingleTeam) {
                await router.push(getPostLoginRoute(getters));
            } else {
                await router.push('/teams');
            }
        } catch (error) {
            // In case of network error or 4xx/5xx we reset auth state and stay on login page
            await dispatch('logout');
            // Ensure we are on login route
            if (router.currentRoute.value.name !== 'login') {
                router.push('/login');
            }
        }
        localStorage.setItem('loginEvent', Date.now().toString());
    },
    setFirstLoggedInSession({ commit }: ActionContext<AuthState, RootState>, value: boolean) {
        commit('setFirstLoggedInSession', value);
    },
    async refreshUserTeams(
        { commit, dispatch }: ActionContext<AuthState, RootState>,
        options: { skipReloadTeam?: boolean } = {},
    ): Promise<void> {
        try {
            const response: TeamResponse | undefined = await $rq.getUserTeams();
            const teamsFromApi = response && Array.isArray(response.teams) ? response.teams : [];

            commit('setTeamList', teamsFromApi);

            if (teamsFromApi.length === 1) {
                const [team] = teamsFromApi;
                if (!options.skipReloadTeam) {
                    dispatch('reloadTeam', team.id);
                }
                commit('SET_CURRENT_TEAM_DETAILS', team);
            }
            commit('setLoggedIn', true);
        } catch (error) {
            commit('setTeamList', []);
            commit('setLoggedIn', false);
        }
    },
    async refreshAccessTokenAndReloadAccessControlState({
        commit,
        dispatch,
    }: ActionContext<AuthState, RootState>) {
        const urlParams = new URLSearchParams(window.location.search);
        const sso = urlParams.get('sso');
        const sig = urlParams.get('sig');
        if (sso && sig) {
            try {
                const ssoResponse = await $rq.loginSso({ sso, sig });
                if (ssoResponse && ssoResponse.data && ssoResponse.data.redirectUrl) {
                    window.location.href = ssoResponse.data.redirectUrl;
                    return;
                }
            } catch (ssoError) {
                console.error('SSO Error:', ssoError);
            }
        } else {
            commit(
                {
                    type: 'setIsStateAccessControlLoaded',
                    isStateAccessControlLoaded: false,
                },
                { root: true },
            );
            const newToken = await $rq.getRenewToken();

            commit({
                type: 'setAccessToken',
                token: newToken,
            });

            // Update Axios headers with the new token
            $rq.setAxiosHeaders({
                Authorization: `Bearer ${newToken}`,
            });

            // Decode token and reschedule auto-refresh
            const decoded = JSON.parse(window.atob(newToken.split('.')[1]));
            scheduleAutoRefresh(decoded.exp, dispatch as unknown as Dispatch);
            await dispatch('setAccessControlInfo', { root: true });
        }
    },

    async logout({ commit, dispatch }: ActionContext<AuthState, RootState>) {
        // Delete auth cookies
        const cookiesKey = window.$cookies?.keys() || [];
        cookiesKey.forEach((value: string) => {
            if (value !== 'lang') {
                window.$cookies?.remove(value, true);
            }
        });
        // Delete axios headers
        $rq.resetAxiosHeaders();
        $rq.disconnectWebSockets();
        localStorage.setItem('logoutEvent', Date.now().toString());
        // We have to reset current lang
        commit('resetAuthState');
        if (refreshTimer) clearTimeout(refreshTimer);
        await dispatch('resetAccessControlState', null, { root: true });
    },
    async switchTeam(
        { commit, dispatch }: ActionContext<AuthState, RootState>,
        team: Team,
    ): Promise<void> {
        try {
            await $rq.getRenewToken(team.id);
            $rq.reconnectWebSockets();
            commit('setSelectedTeam', team);
            commit('SET_CURRENT_TEAM_DETAILS', team);
            await dispatch('reloadTeam', team.id);
            await dispatch('setAccessControlInfo', null, { root: true });
        } catch (error) {
            dispatch('logout');
            throw error;
        }
    },
    decodeUrlInviteData({ commit }: ActionContext<AuthState, RootState>, data: null) {
        let valid = false;
        if (data != null) {
            const decodedData = JSON.parse(Base64.decode(data));
            if (decodedData.team != null && decodedData.invitationCode != null) {
                valid = true;
                commit('setInvitedTeamInfo', decodedData);
                commit('setInvitedUserInfo', decodedData);
                commit('setSignupVerificationCode', decodedData.verificationCode);
            }
            if (decodedData.user != null) {
                valid = true;
            }
        }
        return valid;
    },

    async verifySignupToken(_: unknown, data: string) {
        const decodedData = JSON.parse(window.atob(data));
        try {
            await $rq.getVerificationCodeStatus(decodedData.verificationCode, decodedData.user.id);
            return true;
        } catch {
            return false;
        }
    },

    // Automatically handle token on application start / page refresh
    async checkAuthOnAppInit({ commit, dispatch, getters }: ActionContext<AuthState, RootState>) {
        const accessToken = window.$cookies?.get('accessToken');
        const refreshToken = window.$cookies?.get('refreshToken');

        if (!accessToken || !refreshToken) {
            commit('resetAuthState');
            return;
        }

        try {
            // Validate access token
            const decoded = JSON.parse(window.atob(accessToken.split('.')[1]));
            const currentTime = Math.floor(Date.now() / 1000);
            if (decoded.exp <= currentTime + 60) {
                // If token expires within 1 minute
                try {
                    await dispatch('refreshAccessTokenAndReloadAccessControlState');
                } catch (refreshError) {
                    commit('resetAuthState');
                    return;
                }
            } else {
                // Token still valid, schedule refresh
                scheduleAutoRefresh(decoded.exp, dispatch);
            }

            // Restore user info from token
            const currentToken = window.$cookies?.get('accessToken');
            if (currentToken) {
                const tokenData = JSON.parse(window.atob(currentToken.split('.')[1]));
                commit('restoreUserFromToken', tokenData);
            }

            commit('setLoggedIn', true);
            $rq.setAxiosHeaders({
                Authorization: `Bearer ${window.$cookies?.get('accessToken')}`,
            });
            await dispatch('refreshUserTeams');

            // Restore selected team from cookie or from token
            const savedTeamId = window.$cookies?.get('currentTeamId');
            const tokenTeamId = decoded.team?.id;
            const teamIdToRestore = savedTeamId || tokenTeamId;
            if (teamIdToRestore) {
                const teams = (getters.getTeams || []) as Team[];
                const savedTeam = teams.find((t) => t.id === teamIdToRestore);
                if (savedTeam) {
                    commit('setSelectedTeam', savedTeam);
                    commit('SET_CURRENT_TEAM_DETAILS', savedTeam);
                    await dispatch('reloadTeam', savedTeam.id);
                }
            }

            // Load current user info and access control
            await dispatch('setAccessControlInfo', null, { root: true });

            // Redirect to appropriate page only if user is on login/root
            // Use window.location.pathname to check the actual browser URL, not router state
            const browserPath = window.location.pathname;
            if (browserPath === '/' || browserPath === '/login') {
                const redirectParam = new URLSearchParams(window.location.search).get('redirect');
                let targetRoute = redirectParam ? decodeURIComponent(redirectParam) : '/clients';
                if (!redirectParam) {
                    const teams = (getters.getTeams || []) as Team[];
                    if (teams.length === 0) {
                        targetRoute = '/profile';
                    } else if (teams.length > 1 && getters.getTeamIndex === -1) {
                        targetRoute = '/teams';
                    }
                }
                await router.replace(targetRoute);
            }
        } catch (error) {
            commit('resetAuthState');
        }
    },
    decodeUrlResetPwd({ commit }: ActionContext<AuthState, RootState>, data: null) {
        let valid = false;
        if (data != null) {
            valid = true;
            commit('setResetPwdCode', data);
        }
        return valid;
    },
};

const getters = {
    isLoggedIn: (state: { isLoggedIn: boolean }) => state.isLoggedIn,
    isFirstLoginSession: (state: { isFirstLoginSession: boolean }) => state.isFirstLoginSession,
    getAuthStatus: (state: { status: boolean }) => state.status,
    getUser: (state: { user: unknown }) => state.user,
    user: (state: { user: unknown }) => state.user,
    getCurrentUser: (state: { currentUser: User }) => state.currentUser,
    getEmail: (state: { user: { email: string } }) => state.user.email,
    getTeams: (state: { user: { teams: unknown } }) => state.user.teams,
    getTeamPagination: (state: { user: { teamPagination: unknown } }) => state.user.teamPagination,
    getTeamsCount: (state: { user: { teams: string | unknown[] } }) => state.user.teams.length,
    getTeamIndex: (state: { selectedTeamIndex: number }) => state.selectedTeamIndex,
    getInvitedTeam: (state: { invitedTeam: unknown }) => state.invitedTeam,
    resetPwdCode: (state: { resetPwdCode: string }) => state.resetPwdCode,
    getUserID: (state: { user: { id: string } }) => state.user.id,
    getTokenExpiration: (state: { tokenExpiration: string }) => state.tokenExpiration,
    getVerificationCode: (state: { verificationCode: string }) => state.verificationCode,
    getInvitedUser: (state: { invitedUser: unknown }) => state.invitedUser,
    currentTeam: (state: AuthState): Team | null => state.currentTeam,
    currentTeamName: (state: AuthState): string | null => state.currentTeam?.name || null,
    currentTeamId: (state: AuthState): string | null => state.currentTeam?.id || null,
    getDefaultUserRoute: (state: {
        isLoggedIn: boolean;
        user: { teams: string | string[] };
        selectedTeamIndex: number;
        currentUser: { roles: { key: string }[] };
    }) => {
        if (!state.isLoggedIn) {
            return 'login';
        }
        const teamsNum = state.user.teams.length;
        if (teamsNum === 0) {
            return 'profile';
        }
        if (state.selectedTeamIndex === -1) {
            return 'teams';
        }

        if (
            Array.isArray(state?.currentUser?.roles) &&
            state.currentUser.roles[0].key === roles.FINANCIAL_ADMINISTRATOR.value
        ) {
            return 'administration';
        }

        return 'clients';
    },
    getTeam: (state: { selectedTeamIndex: number; user: { teams: { [x: number]: number } } }) => {
        if (
            state.selectedTeamIndex !== -1 &&
            typeof state.user.teams[state.selectedTeamIndex] !== 'undefined'
        ) {
            return state.user.teams[state.selectedTeamIndex];
        }
        return {};
    },
    getTeamID: (state: {
        selectedTeamIndex: number;
        user: { teams: { [x: string]: { id: string } } };
    }) => {
        if (
            state.selectedTeamIndex !== -1 &&
            typeof state.user.teams[state.selectedTeamIndex] !== 'undefined'
        ) {
            return state.user.teams[state.selectedTeamIndex].id;
        }
        return false;
    },
    getTeamName: (state: {
        selectedTeamIndex: number;
        user: { teams: { [x: string]: { name: string } } };
    }) => {
        if (
            state.selectedTeamIndex !== -1 &&
            typeof state.user.teams[state.selectedTeamIndex] !== 'undefined'
        ) {
            return state.user.teams[state.selectedTeamIndex].name;
        }
        return '';
    },

    hasSelectedTeam: (state: {
        selectedTeamIndex: number;
        user: { teams: { [x: number]: number } };
    }) =>
        state.selectedTeamIndex !== -1 &&
        typeof state.user.teams[state.selectedTeamIndex] !== 'undefined',
};

export default {
    state: defaultState,
    getters,
    actions,
    mutations,
};
